import {
  lineItemResolutionSchema,
  type LineItemResolutionFormValues,
} from "@babackapp/return-widget-components/forms/line-item-resolution.form";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useAvailableResolutions } from "@babackapp/return-widget-components/hooks/use-available-resolutions";
import type { VariantCollection } from "@babackapp/return-widget-components/lib/resolutions.lib";
import { useUpdateLineItem } from "@babackapp/return-widget-components/hooks/use-update-line-item";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import type {
  OrderLineItem,
  Resolution,
  ResolutionOption,
} from "@babackapp/return-widget-components/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useCurrentOrderLineItems } from "./order";
import { useWidgetParams } from "./use-widget-params";

export type ResolutionOptionsFormContext =
  | { kind: "refund-or-discount" }
  | { kind: "exchange" };

export function useResolutionOptionsForm() {
  const navigate = useNavigate();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId, shopId } = useWidgetParams();
  const draft = useDraftReturn(orderId);
  const updateLineItem = useUpdateLineItem(orderId);
  const { data: lineItems = [] } = useCurrentOrderLineItems();
  const { data: order } = usePublicOrder(orderId);

  const lineItem = lineItems.find((li) => li.line_item_id === lineItemId);
  const variantCollection = (order?.variantByLineItemId?.[lineItemId] ??
    null) as VariantCollection | null;
  const availableResolutions = useAvailableResolutions(
    shopId,
    lineItem ?? ({} as OrderLineItem),
    variantCollection,
    order?.opt,
  );
  const exchangeAvailable = availableResolutions.includes("exchange");
  const exchangeCreditAvailable =
    availableResolutions.includes("exchange-credit");
  const discountAvailable = availableResolutions.includes("discount");
  const refundAvailable = availableResolutions.includes("refund");

  const draftLineItem = draft?.lineItems.find(
    (li) => li.lineItemId === lineItemId,
  );

  const resolution: Resolution | null = draftLineItem?.resolution ?? null;

  const isExchange =
    resolution === "exchange" || resolution === "exchange-credit";

  const context: ResolutionOptionsFormContext = isExchange
    ? { kind: "exchange" }
    : { kind: "refund-or-discount" };

  // The draft stores the family ("exchange") until a sub-option is picked, so
  // fall back to exchange-credit whenever the variant swap isn't offered.
  // Otherwise no radio matches and the option's footer stays hidden.
  const defaultExchange =
    resolution === "exchange-credit" || !exchangeAvailable
      ? "exchange-credit"
      : "exchange";
  const defaultResolution = isExchange
    ? defaultExchange
    : (resolution ?? "refund");

  const form = useForm<LineItemResolutionFormValues>({
    resolver: zodResolver(lineItemResolutionSchema),
    defaultValues: {
      resolution: defaultResolution,
      resolutionOption:
        (draftLineItem?.resolutionOption as ResolutionOption | null) ??
        (isExchange
          ? null
          : discountAvailable
            ? "discount-code"
            : "bank-transfer"),
      exchangeVariantId: draftLineItem?.exchangeVariantId ?? null,
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (context.kind === "exchange") {
      const res = values.resolution as "exchange" | "exchange-credit";
      updateLineItem(lineItemId, {
        resolution: res,
        exchangeVariantId: res === "exchange" ? values.exchangeVariantId : null,
      });
    } else {
      updateLineItem(lineItemId, {
        resolution:
          values.resolutionOption === "discount-code" ? "discount" : "refund",
        resolutionOption: values.resolutionOption,
      });
    }
    navigate("../../../summary");
  });

  return {
    form,
    onSubmit,
    updateLineItem,
    draft,
    lineItemId,
    orderId,
    shopId,
    resolution,
    context,
    exchangeAvailable,
    exchangeCreditAvailable,
    discountAvailable,
    refundAvailable,
  };
}
